import React from 'react';
import { Box, Typography } from '@mui/material';
import { BARGAINING_LEVELS, LD, bargainingTheme } from './tokens';

/** What the dialog keeps from the market read for this home. */
export interface MarketSnapshot {
  bargainingScore: number;
  listToSaleRatio: number;
  medianDom: number;
}

/** The subtitle for the Market row: the verdict, or what is happening. */
export const marketSubtitle = (
  snapshot: MarketSnapshot | null,
  loading: boolean
): string => {
  if (snapshot) return bargainingTheme(snapshot.bargainingScore).label;
  if (loading) return 'Reading the local market…';
  return 'Not enough recent sales to read';
};

/**
 * The sale-to-list ratio as the gap a buyer would feel, against the price the
 * sellers first asked: "3% below original asking" rather than "97% of asking",
 * which reads as full price. The API's ratio is taken off the original list
 * price where the feed carries one, so a home that has already been reduced
 * is not read as having another 3% in it. Within half a point it is "near".
 */
export const askingGapLabel = (listToSaleRatio: number): string => {
  const gap = Math.round(Math.abs(100 - listToSaleRatio));
  if (gap === 0) return 'near original asking';
  return listToSaleRatio < 100
    ? `${gap}% below original asking`
    : `${gap}% above original asking`;
};

const InsightRow: React.FC<{
  label: string;
  value: string;
  last?: boolean;
}> = ({ label, value, last = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      fontSize: '13.5px',
      lineHeight: 'normal',
      py: '6px',
      color: LD.ink2,
      borderBottom: last ? 'none' : `1px dashed ${LD.line}`,
    }}
  >
    <span>{label}</span>
    <Box component="b" sx={{ color: LD.ink, textAlign: 'right' }}>
      {value}
    </Box>
  </Box>
);

interface MarketPanelProps {
  snapshot: MarketSnapshot | null;
  loading: boolean;
  dom: number;
  zip: string;
  /** The read is drawn from a radius around the home rather than the ZIP. */
  nearby: boolean;
}

/**
 * The bargaining-power read for this home: the verdict, a five-step meter, and
 * the facts a buyer can act on — what nearby homes actually sold for against
 * their original asking, and how long this one has sat against nearby sales.
 * The point score, the under-contract share, the suggested offer range and the
 * canned next steps stay on the Buyer Prep page: a point score is finer than
 * the read supports, and the offer range has not earned a buyer's trust yet
 * (the sale closed above its top half the time in the eval).
 */
const MarketPanel: React.FC<MarketPanelProps> = ({
  snapshot,
  loading,
  dom,
  zip,
  nearby,
}) => {
  if (!snapshot) {
    return (
      <Typography
        sx={{ fontSize: '13.5px', color: LD.muted, lineHeight: 'normal' }}
      >
        {loading
          ? 'Reading recent sales near this home…'
          : 'There are not enough recent sales near this home to read the market yet.'}
      </Typography>
    );
  }

  const theme = bargainingTheme(snapshot.bargainingScore);
  const rows: Array<{ label: string; value: string }> = [];
  if (snapshot.listToSaleRatio > 0) {
    rows.push({
      label: nearby ? 'Recent sales nearby' : `Recent sales in ${zip}`,
      value: askingGapLabel(snapshot.listToSaleRatio),
    });
  }
  if (dom > 0) {
    rows.push({ label: 'This home', value: `${dom} days on the market` });
  }
  if (dom > 0 && snapshot.medianDom > 0) {
    rows.push({
      label: nearby ? 'Homes sold nearby' : `Homes sold in ${zip}`,
      value: `typically ${snapshot.medianDom} days`,
    });
  }

  return (
    <Box data-testid="market-panel">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: LD.ink }}>
          Bargaining power
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: '12.5px',
            fontWeight: 700,
            borderRadius: '999px',
            px: '11px',
            py: '5px',
            lineHeight: 'normal',
            bgcolor: theme.tint,
            color: theme.ink,
            whiteSpace: 'nowrap',
          }}
        >
          {theme.label}
        </Box>
      </Box>
      <Box
        role="meter"
        aria-label="Room to negotiate"
        aria-valuemin={1}
        aria-valuemax={BARGAINING_LEVELS}
        aria-valuenow={theme.level}
        aria-valuetext={theme.label}
        sx={{
          display: 'flex',
          gap: '4px',
          mt: '8px',
          mb: rows.length > 0 ? '8px' : '4px',
        }}
      >
        {Array.from({ length: BARGAINING_LEVELS }, (_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: '4px',
              bgcolor: index < theme.level ? theme.fill : LD.line,
            }}
          />
        ))}
      </Box>
      {rows.length > 0 && (
        <Box>
          {rows.map((row, index) => (
            <InsightRow
              key={row.label}
              label={row.label}
              value={row.value}
              last={index === rows.length - 1}
            />
          ))}
        </Box>
      )}
      <Typography sx={{ fontSize: '11px', color: LD.muted2, mt: '8px' }}>
        An estimate from recent sales nearby, not a prediction of what this
        seller will accept. Provided by Homepoint for informational purposes.
      </Typography>
    </Box>
  );
};

export default MarketPanel;
